import React, { useState, useRef, useEffect } from 'react';
import { SelectionProvider } from './SelectionContext';
import Sidebar from './Sidebar';
import ChatRuntimeProvider from './ChatRuntimeProvider';
import Thread from './Thread';

const SIDEBAR_MIN = 200;
const SIDEBAR_MAX = 480;
const SIDEBAR_DEFAULT = 256;

export default function ChatScreen() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(SIDEBAR_DEFAULT);

  const handleResizeStart = (e) => {
    if (isCollapsed) return;
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizeStartX.current;
      setSidebarWidth(
        Math.min(
          SIDEBAR_MAX,
          Math.max(SIDEBAR_MIN, resizeStartWidth.current + delta),
        ),
      );
    };
    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    // the cursor has to stick while dragging, even outside the handle
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // phones only: below sm the sidebar eats the thread. Never auto-expands again,
  // that's the user's call.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setIsCollapsed(true);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <SelectionProvider>
      <ChatRuntimeProvider>
        <div className="h-screen min-h-0 flex bg-gray-50 overflow-hidden">
          <Sidebar
            isCollapsed={isCollapsed}
            onToggleCollapse={() => setIsCollapsed((collapsed) => !collapsed)}
            width={sidebarWidth}
          />

          {!isCollapsed && (
            <div
              onMouseDown={handleResizeStart}
              className={`w-1.5 flex-shrink-0 cursor-col-resize bg-gray-200 hover:bg-primary-400 active:bg-primary-500 transition-colors ${isResizing ? 'bg-primary-500' : ''}`}
              title="Drag to resize sidebar"
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={sidebarWidth}
              aria-valuemin={SIDEBAR_MIN}
              aria-valuemax={SIDEBAR_MAX}
            />
          )}

          <Thread />
        </div>
      </ChatRuntimeProvider>
    </SelectionProvider>
  );
}
